import { useState } from 'react'
import { AlertTriangle, Eye, EyeOff, LogIn } from 'lucide-react'
import { useMsal } from '@azure/msal-react'
import credenceLogo from '../assets/logo.webp'
import { LOGIN_SCOPES } from '../auth.js'

function PlaceholderLogin({ adminPassword, adminUsername, onLogin }) {
  const [username, setUsername] = useState('')
  const [password, setPassword] = useState('')
  const [rememberMe, setRememberMe] = useState(false)
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState('')
  const [showPassword, setShowPassword] = useState(false)

  const handleSubmit = async (event) => {
    event.preventDefault()
    setError('')

    if (!username || !password) {
      setError('Please enter both username and password')
      return
    }

    setIsLoading(true)

    try {
      await new Promise((resolve) => setTimeout(resolve, 500))

      if (username !== adminUsername || password !== adminPassword) {
        setError('Invalid username or password')
        return
      }

      const userData = {
        username,
        email: username,
        name: username,
        token: `placeholder-token-${Date.now()}`,
        loginTime: new Date().toISOString(),
      }

      if (rememberMe) {
        localStorage.setItem('msr-auth', JSON.stringify(userData))
      } else {
        sessionStorage.setItem('msr-auth', JSON.stringify(userData))
      }

      onLogin(userData)
    } catch {
      setError('Login failed. Please try again.')
    } finally {
      setIsLoading(false)
    }
  }

  return (
    <form onSubmit={handleSubmit} className="login-form">
      <div className="form-group">
        <label htmlFor="username">Username</label>
        <input
          id="username"
          type="text"
          value={username}
          onChange={(event) => setUsername(event.target.value)}
          placeholder="Enter your username"
          className="form-input"
          disabled={isLoading}
          autoComplete="username"
          title="Enter your username"
        />
      </div>

      <div className="form-group">
        <label htmlFor="password">Password</label>
        <div className="input-password-wrap">
          <input
            id="password"
            type={showPassword ? 'text' : 'password'}
            value={password}
            onChange={(event) => setPassword(event.target.value)}
            placeholder="Enter your password"
            className="form-input"
            disabled={isLoading}
            autoComplete="current-password"
            title="Enter your password"
          />
          <button
            type="button"
            className="password-toggle"
            onClick={() => setShowPassword((visible) => !visible)}
            aria-label={showPassword ? 'Hide password' : 'Show password'}
            tabIndex={-1}
          >
            {showPassword ? <EyeOff size={16} /> : <Eye size={16} />}
          </button>
        </div>
      </div>

      <div className="form-group-checkbox">
        <input
          id="remember"
          type="checkbox"
          checked={rememberMe}
          onChange={(event) => setRememberMe(event.target.checked)}
          disabled={isLoading}
          className="form-checkbox"
          title="Keep your login in local storage on this machine"
        />
        <label htmlFor="remember" className="checkbox-label">
          Remember me
        </label>
      </div>

      {error ? (
        <div className="login-error">
          <span className="error-icon" aria-hidden="true">
            <AlertTriangle size={18} />
          </span>
          <span>{error}</span>
        </div>
      ) : null}

      <button type="submit" className="btn btn-login" disabled={isLoading} title="Sign in to the MSR Processor">
        {isLoading ? (
          <>
            <span className="spinner" />
            Signing in...
          </>
        ) : (
          <>
            <LogIn size={18} aria-hidden="true" />
            Sign In
          </>
        )}
      </button>
    </form>
  )
}

function OAuthLogin() {
  const { instance } = useMsal()

  const handleSignIn = async () => {
    await instance.loginRedirect({
      scopes: LOGIN_SCOPES,
    })
  }

  return (
    <div className="login-form">
      <button type="button" className="btn btn-login" onClick={handleSignIn} title="Sign in with Credence-llc account">
        <LogIn size={18} aria-hidden="true" />
        Sign in with Credence-llc account
      </button>
    </div>
  )
}

function Login({
  authMode = 'oauth',
  adminPassword = '',
  adminUsername = '',
  onLogin = () => {},
}) {
  const isPlaceholder = authMode === 'placeholder'

  return (
    <div className="login-container">
      <div className="login-card">
        <div className="login-brand-section">
          <div className="login-logo-wrap">
            <img
              src={credenceLogo}
              alt="Credence"
              className="login-brand-logo"
              width="313"
              height="256"
              decoding="async"
              fetchPriority="high"
            />
          </div>
        </div>

        <div className="login-form-section">
          <div className="login-header">
            <h1>MSR Processor</h1>
            <p>
              {isPlaceholder
                ? 'Sign in to access your documents'
                : 'Sign in with your Credence-llc account to access documents.'}
            </p>
          </div>

          {isPlaceholder ? (
            <PlaceholderLogin
              adminPassword={adminPassword}
              adminUsername={adminUsername}
              onLogin={onLogin}
            />
          ) : (
            <OAuthLogin />
          )}

          <div className="login-footer">
            <p>Secure document processing system</p>
          </div>
        </div>
      </div>
    </div>
  )
}

export default Login
